import { getMonth, isAfter } from 'date-fns'
import { createSelector } from 'reselect'
import queryString from 'query-string'
import moment from 'moment'
import { List } from 'immutable'

import { prettyShortDate } from 'utils/date'
import { UNAUTHORIZED } from 'userTypes'

export * from './monthAvailabilities'
export * from './search'
export * from './consultant'
export * from './experience'
export * from './chat'

export const isAdminSelector = state => state.auth.user.type === 'admin'

export const userInfosSelector = createSelector(
  state => state.userInfo,
  userInfo => Object.keys(userInfo)
)

export const unlistedConsultantsSelector = state => {
  try {
    return Object.keys(state.consultants).filter(
      id => !state.consultants[id].listed && !state.consultants[id].hidden
    )
  } catch (e) {
    return undefined
  }
}

export const hasRedirectSelector = (state, props) => {
  try {
    return !!props.history.location.state.from
  } catch (e) {
    return false
  }
}

export const isAuthenticatedSelector = state => state.auth.authenticated

export const isAuthorizedSelector = (state, userTypes) => {
  try {
    return userTypes.includes(state.auth.user.type)
  } catch (e) {
    return false
  }
}

export const isLoggedInSelector = state => state.auth.authenticated

export const profilePictureSelector = (state, userId) => {
  try {
    return state.userInfo[userId || currentUserIdSelector(state)].profilePicture
  } catch (e) {
    return undefined
  }
}

export const userIdPending = (state, userId) => {
  try {
    return state.userInfo[userId].pending
  } catch (e) {
    return undefined
  }
}

export const currentUserIdSelector = state => {
  try {
    return state.auth.user.id
  } catch (e) {
    return undefined
  }
}

export const settingsSelector = state => state.settings

export const makeRoomUserIdsSelector = () =>
  createSelector(
    [(state, props) => state.rooms[props.roomId].members],
    members => Object.keys(members)
  )

export const roomMessageIdsSelector = (state, roomId) => {
  try {
    return Object.keys(state.messages[roomId])
  } catch (e) {
    return undefined
  }
}

export const messageSelector = (state, roomId, messageId) => {
  try {
    return state.messages[roomId][messageId]
  } catch (e) {
    return undefined
  }
}

export const isMessageSelfSelector = (state, roomId, messageId) => {
  try {
    return isSelfSelector(
      state,
      messageSelector(state, roomId, messageId).userId
    )
  } catch (e) {
    return undefined
  }
}

export const messageAuthorNameSelector = (state, roomId, messageId) => {
  try {
    return state.userInfo[
      messageSelector(state, roomId, messageId).userId
    ].name.split(' ')[0]
  } catch (e) {
    return undefined
  }
}

export const messageTimeSelector = (state, roomId, messageId) => {
  try {
    const time = moment(messageSelector(state, roomId, messageId).timestamp)
    const month = months[Number(time.format('M')) - 1]
    return `${time.format('DD')}. ${month}, ${time.format('HH:mm')}`
  } catch (e) {
    return undefined
  }
}

export const roomUserInfoSelector = (state, roomId) => {
  try {
    return state.userInfo[roomUserIdSelector(state, roomId)]
  } catch (e) {
    return undefined
  }
}

export const roomNameSelector = (state, roomId) => {
  try {
    if (state.rooms[roomId].type === 'feedback') {
      if (
        state.auth.user.type === 'customer' ||
        state.auth.user.type === 'consultant'
      ) {
        return 'Spir-teamet'
      }
      if (state.auth.user.type === 'admin') {
        return roomNonAdminUserInfoSelector(state, roomId).name
      }
    }
    return roomUserInfoSelector(state, roomId).name
  } catch (e) {
    return undefined
  }
}

export const roomUsersSelector = (state, roomId) => {
  try {
    return Object.keys(state.rooms[roomId].members)
      .filter(id => id !== state.auth.user.id)
      .map(id => ({
        id,
        name: state.userInfo[id].name,
      }))
  } catch (e) {
    return undefined
  }
}

export const roomUserIdSelector = (state, roomId) => {
  try {
    if (state.rooms[roomId].type === 'feedback') {
      return Object.keys(state.rooms[roomId].members).find(
        user =>
          state.userInfo[user].type !== 'admin' && user !== state.auth.user.id
      )
    } else {
      return Object.keys(state.rooms[roomId].members).find(
        user => user !== state.auth.user.id
      )
    }
  } catch (e) {
    return undefined
  }
}

export const roomTitleSelector = (state, roomId) => {
  try {
    if (state.rooms[roomId].type === 'feedback') {
      if (
        state.auth.user.type === 'customer' ||
        state.auth.user.type === 'consultant'
      ) {
        return 'Fredrik, Jonas, Line, Martine og Sindre'
      }
      if (state.auth.user.type === 'admin') {
        const userInfo = roomNonAdminUserInfoSelector(state, roomId)
        const titles = userInfo.titles.join(', ')
        const employer = userInfo.employer
        return `${titles}${employer ? ' i ' + userInfo.employer : ''}`
      }
    }
    const userInfo = roomUserInfoSelector(state, roomId)
    const titles = userInfo.titles.join(', ')
    const employer = userInfo.employer
    return `${titles}${employer ? ' i ' + userInfo.employer : ''}`
  } catch (e) {
    return undefined
  }
}

export const roomProfilePicture = (state, roomId) => {
  try {
    if (state.rooms[roomId].type === 'feedback') {
      if (
        state.auth.user.type === 'customer' ||
        state.auth.user.type === 'consultant'
      ) {
        return '/icons/spir_profile.svg'
      }
      if (state.auth.user.type === 'admin') {
        return roomNonAdminUserInfoSelector(state, roomId).profilePicture
      }
    }
    return roomUserInfoSelector(state, roomId).profilePicture
  } catch (e) {
    return undefined
  }
}

export const roomNonAdminUserInfoSelector = (state, roomId) => {
  try {
    return state.userInfo[
      Object.keys(state.rooms[roomId].members).find(
        user => state.userInfo[user].type !== 'admin'
      )
    ]
  } catch (e) {
    return undefined
  }
}

export const roomLastMessageSelector = (state, roomId) => {
  try {
    const message = state.rooms[roomId].lastMessage
    const name = state.userInfo[message.userId].name.split(' ')[0]

    return `${name}: ${message.message}`
  } catch (e) {
    return undefined
  }
}

export const messageAuthorProfilePictureSelector = (
  state,
  roomId,
  messageId
) => {
  try {
    return state.userInfo[messageSelector(state, roomId, messageId).userId]
      .profilePicture
  } catch (e) {
    return undefined
  }
}

export const sortedUserRoomIdsSelector = createSelector(
  state => state.auth.user.rooms,
  state => state.rooms,
  (userRooms, rooms) =>
    List(Object.keys(userRooms))
      .sort((a, b) => {
        const bTime = rooms[b]
          ? rooms[b].lastMessage ? rooms[b].lastMessage.timestamp : 0
          : 0
        const aTime = rooms[a]
          ? rooms[a].lastMessage ? rooms[a].lastMessage.timestamp : 0
          : 0
        return bTime - aTime
      })
      .toArray()
)

export const roomLastMessageTimestampSelector = (state, roomId) => {
  try {
    const lastMessage = state.rooms[roomId].lastMessage
    return lastMessage ? lastMessage.timestamp : 0
  } catch (e) {
    return undefined
  }
}

export const roomLastMessageTimeSelector = (state, roomId) => {
  try {
    const timestamp = roomLastMessageTimestampSelector(state, roomId)
    return timestamp ? prettyShortDate(moment(timestamp)) : ''
  } catch (e) {
    return undefined
  }
}

export const newUserSelector = state => {
  try {
    return state.auth.user.newUser
  } catch (e) {
    return undefined
  }
}

export const userTypeSelector = (state, userId) => {
  try {
    return (
      (userId ? state.userInfo[userId].type : state.auth.user.type) ||
      UNAUTHORIZED
    )
  } catch (e) {
    return UNAUTHORIZED
  }
}

export const userListedSelector = state => {
  try {
    return state.auth.user.listed
  } catch (e) {
    return false
  }
}

export const savedConsultantIdsSelector = createSelector(
  state => state.consultants,
  consultants => Object.keys(consultants).filter(id => consultants[id].saved)
)

export const searchResultSelector = createSelector(
  state => state.search.results,
  state => state.search.query,
  state => state.search.available,
  (results, query, date) => {
    try {
      return results[query]
        .filter(
          consultant =>
            !date ||
            (consultant.availability &&
              (consultant.availability.type === 'now' ||
                (consultant.availability.type === 'date' &&
                  !isAfter(consultant.availability.date, date))))
        )
        .map(({ id }) => id)
    } catch (e) {
      return undefined
    }
  }
)

export const availableSelector = state => state.search.available

const months = [
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
]

export const availableValueSelector = createSelector(
  availableSelector,
  available =>
    available
      ? getMonth(new Date()) === getMonth(available)
        ? 'nå'
        : months[getMonth(available)]
      : 'Vis alle'
)

export const searchInputValueSelector = createSelector(
  state => state.search.activeSuggestion,
  state => state.search.input,
  state => state.search.suggestions,
  (activeSuggestion, input, suggestions) =>
    activeSuggestion === undefined ? input : suggestions[activeSuggestion]
)

export const logInErrorSelector = createSelector(
  state => state.auth.logInError,
  logInError => (logInError ? logInError.general : undefined)
)

export const logInEmailErrorSelector = createSelector(
  state => state.auth.logInError,
  logInError => (logInError ? logInError.email : undefined)
)

export const logInPasswordErrorSelector = createSelector(
  state => state.auth.logInError,
  logInError => (logInError ? logInError.password : undefined)
)

export const registerErrorSelector = createSelector(
  state => state.auth.registerError,
  registerError => (registerError ? registerError.general : undefined)
)

export const registerEmailErrorSelector = createSelector(
  state => state.auth.registerError,
  registerError => (registerError ? registerError.email : undefined)
)

export const registerPasswordErrorSelector = createSelector(
  state => state.auth.registerError,
  registerError => (registerError ? registerError.password : undefined)
)

export const resetPasswordErrorSelector = state => state.auth.resetPasswordError

export const resetPasswordSuccessSelector = state => state.auth.resetPassword

export const userNameSelector = (state, userId) => {
  try {
    return userId
      ? state.userInfo[userId].name
      : state.userInfo[currentUserIdSelector(state)].name.split(' ')[0]
  } catch (e) {
    return undefined
  }
}

export const fullNameSelector = (state, userId) => {
  try {
    return state.userInfo[userId || currentUserIdSelector(state)].name
  } catch (e) {
    return undefined
  }
}

export const titlesSelector = (state, userId) => {
  try {
    return state.userInfo[userId || currentUserIdSelector(state)].titles
  } catch (e) {
    return undefined
  }
}

export const employerSelector = (state, userId) => {
  try {
    return state.userInfo[userId || currentUserIdSelector(state)].employer
  } catch (e) {
    return undefined
  }
}

export const queryStringSelector = ({ search: { query, available } }) => {
  try {
    return query.length || available
      ? `?${queryString.stringify({
          query: query.length ? query : undefined,
          available,
        })}`
      : ''
  } catch (e) {
    return ''
  }
}

export const isSelfSelector = (state, consultantId) =>
  state.auth.user.id === consultantId

export const tagSuggestionsSelector = (state, { input }) => {
  try {
    return state.tagSuggestions[input].slice(0, 5)
  } catch (e) {
    return undefined
  }
}

export const tagsSelector = state => state.tags

export const updateUserInfoPendingSelector = state => {
  try {
    return state.userInfo.pending
  } catch (e) {
    return undefined
  }
}



// WEBPACK FOOTER //
// ./src/selectors/index.js